import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Router, CalendarDays, IndianRupee } from "lucide-react";
import { PageHeader, FadeUp } from "../components/Reveal";
import { ComboBanner } from "../components/ComboBanner";
import { PLANS } from "../data/siteData";

const PlanCard = ({ plan, index }) => (
    <FadeUp delay={index * 0.08} className="h-full">
        <div
            data-testid={`plan-card-${plan.id}mbps`}
            className={`relative flex h-full flex-col overflow-hidden rounded-3xl border p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-2 ${
                plan.highlight
                    ? "border-[#E61C24]/60 bg-gradient-to-b from-[#E61C24]/18 via-[#131927] to-[#131927] shadow-[0_0_54px_rgba(230,28,36,0.22)]"
                    : "glass hover:border-white/25"
            }`}
        >
            {plan.highlight && (
                <motion.span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent"
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                />
            )}
            <div className="flex items-center justify-between">
                <span
                    className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold tracking-[0.26em] ${
                        plan.highlight
                            ? "bg-[#E61C24] text-white"
                            : "bg-white/8 text-[#00F0FF]"
                    }`}
                >
                    {plan.tag}
                </span>
                {plan.highlight && (
                    <span className="font-mono text-[10px] font-semibold tracking-widest text-[#00F0FF]">
                        HIGH SPEED
                    </span>
                )}
            </div>

            <p className="font-display mt-6 text-5xl font-black text-white">
                {plan.speed}
                <span className="ml-1.5 text-lg font-bold text-slate-400">Mbps</span>
            </p>
            <p className="mt-2 text-sm text-slate-400">{plan.blurb}</p>

            <p
                className="mt-6 font-mono text-3xl font-bold text-white"
                data-testid={`plan-price-${plan.id}mbps`}
            >
                ₹{plan.monthly}
                <span className="text-sm font-medium text-slate-400"> / Month</span>
            </p>

            <div className="mt-6 space-y-2.5 rounded-2xl border border-white/10 bg-black/25 p-4">
                <p className="flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.22em] text-slate-400">
                    <CalendarDays className="h-3.5 w-3.5 text-[#00F0FF]" />
                    6 MONTH PLANS
                </p>
                <p className="flex items-center justify-between text-sm text-slate-300">
                    <span>Without router</span>
                    <span className="font-mono font-bold text-white">
                        ₹{plan.sixNoRouter.toLocaleString("en-IN")}
                    </span>
                </p>
                <p className="flex items-center justify-between text-sm text-slate-300">
                    <span className="flex items-center gap-1.5">
                        <Router className="h-3.5 w-3.5 text-[#ff5a60]" />
                        With router
                    </span>
                    <span className="font-mono font-bold text-white">
                        ₹{plan.sixRouter.toLocaleString("en-IN")}
                    </span>
                </p>
                {plan.installation && (
                    <p className="flex items-center justify-between border-t border-white/10 pt-2.5 text-sm text-slate-300">
                        <span>Installation</span>
                        <span className="font-mono font-bold text-[#00F0FF]">
                            ₹{plan.installation}
                        </span>
                    </p>
                )}
            </div>

            <ul className="mt-6 flex-1 space-y-2.5">
                {plan.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#00F0FF]" />
                        {b}
                    </li>
                ))}
            </ul>

            <Link
                to={`/get-connected?plan=${plan.speed}`}
                data-testid={`plan-select-${plan.id}mbps-button`}
                className={`group mt-7 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-extrabold transition-[transform,background-color,color,box-shadow] duration-200 hover:scale-[1.03] ${
                    plan.highlight
                        ? "bg-[#E61C24] text-white shadow-[0_10px_36px_rgba(230,28,36,0.45)] hover:bg-[#C7151C]"
                        : "border border-white/20 text-white hover:border-[#00F0FF]/70 hover:text-[#00F0FF]"
                }`}
            >
                Get This Plan
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
        </div>
    </FadeUp>
);

const ComparisonTable = () => (
    <FadeUp>
        <div
            data-testid="plans-comparison-table"
            className="glass mt-16 overflow-hidden rounded-3xl"
        >
            <div className="border-b border-white/10 px-6 py-5">
                <h2 className="font-display text-xl font-bold text-white sm:text-2xl">
                    Compare All Plans
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                    Simple plans. Reliable speed. Powerful connectivity.
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] text-left text-sm">
                    <thead>
                        <tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                            <th className="px-6 py-4 font-semibold">Plan</th>
                            <th className="px-6 py-4 font-semibold">Speed</th>
                            <th className="px-6 py-4 font-semibold">Monthly</th>
                            <th className="px-6 py-4 font-semibold">6 Mo (w/o Router)</th>
                            <th className="px-6 py-4 font-semibold">6 Mo (With Router)</th>
                            <th className="px-6 py-4 font-semibold">Installation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {PLANS.map((p) => (
                            <tr
                                key={p.id}
                                data-testid={`compare-row-${p.id}mbps`}
                                className={`border-b border-white/5 transition-colors duration-200 hover:bg-white/5 ${
                                    p.highlight ? "bg-[#E61C24]/5" : ""
                                }`}
                            >
                                <td className="px-6 py-4">
                                    <span
                                        className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] ${
                                            p.highlight
                                                ? "bg-[#E61C24]/20 text-[#ff6b70]"
                                                : "bg-white/8 text-slate-300"
                                        }`}
                                    >
                                        {p.tag}
                                    </span>
                                </td>
                                <td className="px-6 py-4 font-display text-base font-bold text-white">
                                    {p.speed} Mbps
                                </td>
                                <td className="px-6 py-4 font-mono font-bold text-white">
                                    ₹{p.monthly}
                                </td>
                                <td className="px-6 py-4 font-mono text-slate-300">
                                    ₹{p.sixNoRouter.toLocaleString("en-IN")}
                                </td>
                                <td className="px-6 py-4 font-mono text-slate-300">
                                    ₹{p.sixRouter.toLocaleString("en-IN")}
                                </td>
                                <td className="px-6 py-4 font-mono text-slate-300">
                                    {p.installation ? `₹${p.installation}` : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="px-6 py-4 text-xs text-slate-500">
                <IndianRupee className="mr-1 inline h-3.5 w-3.5 text-slate-500" />
                All prices in INR. TV channels and OTT packs can be added to any plan —
                ask customer care for the current combo offers.
            </p>
        </div>
    </FadeUp>
);

export default function Plans() {
    return (
        <>
            <PageHeader
                testId="plans-header"
                eyebrow="BROADBAND PLANS"
                title={
                    <>
                        Choose Your <span className="text-gradient-fire">Perfect Speed</span>
                    </>
                }
                subtitle="Simple plans. Reliable speed. Powerful connectivity."
            />
            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {PLANS.map((p, i) => (
                        <PlanCard key={p.id} plan={p} index={i} />
                    ))}
                </div>
                <ComparisonTable />
            </section>
            <ComboBanner />
        </>
    );
}
